import { useState, useEffect, useCallback, useRef } from 'react';
import { SpeechRecognitionResult as AppSpeechRecognitionResult } from '../types';

interface SpeechRecognitionEvent extends Event {
  results: SpeechRecognitionResultList;
  resultIndex: number;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface WebSpeechRecognitionResult {
  0: SpeechRecognitionAlternative;
  isFinal: boolean;
  length: number;
}

interface RecognitionResult {
  transcript: string;
  confidence: number;
  isFinal: boolean;
}

declare global {
  interface Window {
    SpeechRecognition: any;
    webkitSpeechRecognition: any;
  }
}

// Words that are commonly misheard and should be standardized
const COMMONLY_MISHEARD: Record<string, string> = {
  'then': 'then',
  'den': 'then',
  'thin': 'then',
  'when': 'when',
  'win': 'when',
  'wind': 'when',
  'if': 'if',
  'of': 'if',
  'ef': 'if',
  'first': 'first',
  '1st': 'first',
  'fist': 'first',
  'next': 'next',
  'nest': 'next',
  'nets': 'next',
  'finally': 'finally',
  'find': 'finally',
  'finely': 'finally'
};

// Clean up common speech recognition errors in the transcript
const cleanTranscript = (text: string): string => {
  if (!text) return '';
  
  // Convert to lowercase for processing
  let cleaned = text.toLowerCase().trim();
  
  // Replace common misheard words
  Object.entries(COMMONLY_MISHEARD).forEach(([misheard, correction]) => {
    const regex = new RegExp(`\\b${misheard}\\b`, 'gi');
    cleaned = cleaned.replace(regex, correction);
  });
  
  // Remove filler words
  cleaned = cleaned.replace(/\b(um|uh|eh|ah|like|you know|actually|basically|literally)\b/gi, '');
  
  // Clean up double spaces
  cleaned = cleaned.replace(/\s+/g, ' ').trim();
  
  return cleaned;
};

const useSpeechRecognition = () => {
  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimResult, setInterimResult] = useState('');
  const [error, setError] = useState<string | null>(null);
  
  // Use refs to maintain instance across renders
  const recognitionRef = useRef<any>(null);
  const isListeningRef = useRef<boolean>(false);
  
  // Store confidence levels - lower threshold for better detection
  const confidenceThreshold = 0.05; // Lower threshold to capture more speech
  
  const onResultsUpdate = useCallback(
    (results: RecognitionResult) => {
      const resultText = cleanTranscript(results.transcript);
      
      // Log what's being captured for debugging
      console.log('Speech detected:', resultText, 
                 'Final:', results.isFinal, 
                 'Confidence:', results.confidence);
      
      // For final results
      if (results.isFinal) {
        // Skip empty results
        if (resultText.length < 1) return;
        
        // Accept almost all results but prioritize higher confidence ones
        setTranscript(prev => {
          const newTranscript = `${prev} ${resultText}`.trim();
          return newTranscript;
        });
        
        setInterimResult('');
      } else {
        // Show all interim results to help understand what's being captured
        setInterimResult(resultText);
      }
    },
    []
  );

  // Initialize speech recognition once on mount
  useEffect(() => {
    // Initialize speech recognition
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setError('Speech recognition is not supported in this browser');
      console.error('Speech recognition is not supported in this browser.');
      return;
    }
    
    try {
      const recognitionInstance = new SpeechRecognition();
      
      // Basic configuration
      recognitionInstance.continuous = true;
      recognitionInstance.interimResults = true;
      recognitionInstance.lang = 'en-US';
      recognitionInstance.maxAlternatives = 3; // Try to get multiple interpretations
      
      // Simpler configuration to ensure basics work
      recognitionInstance.onresult = (event: SpeechRecognitionEvent) => {
        try {
          // Process each result
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const result = event.results[i];
            const transcript = result[0].transcript;
            const confidence = result[0].confidence;
            
            console.log(`Recognition result ${i}: "${transcript}" (confidence: ${confidence})`);
            
            onResultsUpdate({
              transcript,
              confidence,
              isFinal: result.isFinal
            });
          }
        } catch (err) {
          console.error('Error processing speech results:', err);
          setError(`Speech processing error: ${err}`);
        }
      };
      
      // Handle errors
      recognitionInstance.onerror = (event: SpeechRecognitionErrorEvent) => {
        console.error('Speech recognition error:', event.error);
        setError(`Speech recognition error: ${event.error}`);
        
        // Only update state if we're still mounted
        setIsListening(false);
        isListeningRef.current = false;
        
        // Try to restart on non-fatal errors
        if (isListeningRef.current && event.error !== 'not-allowed' && event.error !== 'aborted') {
          setTimeout(() => {
            try {
              recognitionInstance.start();
              console.log('Restarted after error');
            } catch (e) {
              console.error('Failed to restart after error:', e);
            }
          }, 1000);
        }
      };
      
      // Handle end of recognition
      recognitionInstance.onend = () => {
        console.log('Speech recognition ended');
        
        // Auto restart if we still want to be listening
        if (isListeningRef.current) {
          try {
            recognitionInstance.start();
            console.log('Restarted speech recognition');
          } catch (e) {
            console.error('Failed to restart recognition:', e);
            setError(`Failed to restart: ${e}`);
            setIsListening(false);
            isListeningRef.current = false;
          }
        } else {
          setIsListening(false);
        }
      };
      
      // Store the instance in the ref
      recognitionRef.current = recognitionInstance;
      
      console.log('Speech recognition initialized successfully');
    } catch (err) {
      console.error('Error setting up speech recognition:', err);
      setError(`Setup error: ${err}`);
    }
    
    // Cleanup on unmount
    return () => {
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
          console.log('Speech recognition aborted on cleanup');
        } catch (err) {
          console.error('Error aborting recognition:', err);
        }
      }
    };
  }, []); // Empty dependency array means this runs once on mount

  const startListening = useCallback(() => {
    setError(null);
    
    if (!recognitionRef.current) {
      setError('Speech recognition not initialized');
      console.error('Speech recognition not initialized');
      return;
    }
    
    try {
      // Reset state
      setTranscript('');
      setInterimResult('');
      
      // Stop any existing session first
      try {
        recognitionRef.current.stop();
      } catch (e) {
        // Ignore errors here, as it might not be running
      }
      
      // Short delay to ensure any previous session is fully stopped
      setTimeout(() => {
        try {
          console.log('Starting speech recognition...');
          recognitionRef.current.start();
          setIsListening(true);
          isListeningRef.current = true;
        } catch (startErr) {
          console.error('Error starting after delay:', startErr);
          setError(`Failed to start: ${startErr}`);
        }
      }, 100);
    } catch (err) {
      setError(`Failed to start listening: ${err}`);
      console.error('Error starting speech recognition:', err);
    }
  }, []);

  const stopListening = useCallback(() => {
    if (!recognitionRef.current) {
      return;
    }
    
    isListeningRef.current = false;
    
    try {
      console.log('Stopping speech recognition...');
      recognitionRef.current.stop();
    } catch (err) {
      console.error('Error stopping speech recognition:', err);
    }
    
    setIsListening(false);
  }, []);

  return {
    isListening,
    startListening,
    stopListening,
    transcript,
    interimResult,
    error
  };
};

export default useSpeechRecognition; 